import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { bem } from 'helpers/class-name';

import { DayHourHeatmapGrid } from 'components/DayHourHeatmap';
import Message from 'components/Message';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';

import { useSmartRateFaqsQuery } from '../SmartRate.hooks';
import { SMART_RATE_FAQ_IDS } from '../SmartRateFaqs.constants';
import { isSmartRateFaqPublished, navigateToSmartRateFaq } from '../SmartRateFaqs.helpers';
import messages from './WeeklyEarnings.messages';
import { buildEarningsFormatter, CELL_STATE, getEarningsColor } from './WeeklyEarnings.helpers';
import WeeklyEarningsTooltip, { resolveCta, tooltipShape, TOOLTIP_VARIANT } from './WeeklyEarningsTooltip';

const { element } = bem('weekly-earnings');

const HEATMAP_TOOLTIP_NAME = 'we-heatmap-tooltip';

export function buildTooltip({
  cell,
  rowTime,
  dayLabel,
  averagePayoutRate,
  totalEarnings,
  currency,
  hasUnfilledSpotsFaq,
  onUnfilledSpotsClick,
}) {
  const title = `${rowTime} ${dayLabel} performance`;
  const periodSubtitle = `${dayLabel} • ${rowTime}`;

  if (cell.state === CELL_STATE.NO_SPOTS) {
    return { title, periodSubtitle, variant: TOOLTIP_VARIANT.NO_SPOTS };
  }

  if (cell.state === CELL_STATE.NO_CP_BOOKINGS) {
    return { title, periodSubtitle, variant: TOOLTIP_VARIANT.NO_CP_BOOKINGS };
  }

  if (cell.state !== CELL_STATE.EARNINGS) return null;

  const stats = [
    {
      key: 'total-earnings',
      label: { message: messages.tooltipStatTotalEarnings },
      value: totalEarnings != null ? currency.format(totalEarnings) : currency.format(cell.value),
    },
    {
      key: 'average-payout-rate',
      label: { message: messages.tooltipStatAveragePayoutRate },
      value: averagePayoutRate != null ? currency.format(averagePayoutRate) : '—',
    },
    {
      key: 'reservations',
      label: { message: messages.tooltipStatReservations },
      value: cell.classpassBooked != null ? String(cell.classpassBooked) : '—',
    },
    {
      key: 'unfilled-spots',
      label: {
        message: messages.tooltipStatUnfilledSpots,
        values: {
          a: text =>
            hasUnfilledSpotsFaq ? (
              <button
                className={element('inline-link')}
                onClick={() =>
                  navigateToSmartRateFaq(SMART_RATE_FAQ_IDS.UNFILLED_SPOTS, { beforeScroll: onUnfilledSpotsClick })
                }
                type="button">
                {text}
              </button>
            ) : (
              text
            ),
        },
      },
      value: cell.excessSpotsFilledPercent != null ? `${Math.round(cell.excessSpotsFilledPercent)}%` : '—',
    },
  ];

  const ctaDestination = resolveCta(cell.excessSpotsFilledPercent, cell.classpassBooked);

  return { title, periodSubtitle, variant: TOOLTIP_VARIANT.EARNINGS, stats, ctaDestination };
}

function HeatmapCell({ cell, currency, currencyCompact, tooltip, tooltipName, isMobile, onMobileTap, onMouseEnter }) {
  const intl = useIntl();

  const renderLabelCell = message => {
    const label = intl.formatMessage(message);
    return (
      <div className={element('cell', { label: true })} title={label}>
        <span className={element('cell-label-text')}>{label}</span>
        <Message as="span" className={element('cell-label-mobile')} message={messages.cellEmpty} />
      </div>
    );
  };

  const renderCellContent = () => {
    switch (cell.state) {
      case CELL_STATE.EARNINGS: {
        const palette = getEarningsColor(cell.value);
        const fullValue = currency.format(cell.value);
        return (
          <div
            className={element('cell', {
              earnings: true,
              light: palette.text === 'light',
              dark: palette.text === 'dark',
            })}
            style={{ backgroundColor: palette.background }}>
            <span className={element('cell-earnings-full')}>{fullValue}</span>
            <span className={element('cell-earnings-mobile')} title={fullValue}>
              {currencyCompact.format(cell.value)}
            </span>
          </div>
        );
      }
      case CELL_STATE.NO_SPOTS:
        return renderLabelCell(messages.cellNoSpots);
      case CELL_STATE.NO_CP_BOOKINGS:
        return renderLabelCell(messages.cellNoCpBookings);
      case CELL_STATE.EMPTY:
      default:
        return <div aria-hidden className={element('cell', { spacer: true })} />;
    }
  };

  if (!tooltip || cell.state === CELL_STATE.EMPTY) {
    return renderCellContent();
  }

  if (isMobile) {
    return (
      <button className={element('cell-tap-target')} onClick={onMobileTap} type="button" aria-label={tooltip.title}>
        {renderCellContent()}
      </button>
    );
  }

  return (
    <TooltipToggle className={element('cell-toggle')} name={tooltipName} onMouseEnter={onMouseEnter}>
      {renderCellContent()}
    </TooltipToggle>
  );
}
HeatmapCell.propTypes = {
  cell: PropTypes.shape({ state: PropTypes.string, value: PropTypes.number }).isRequired,
  currency: PropTypes.shape({ format: PropTypes.func.isRequired }).isRequired,
  currencyCompact: PropTypes.shape({ format: PropTypes.func.isRequired }).isRequired,
  tooltip: tooltipShape,
  tooltipName: PropTypes.string,
  isMobile: PropTypes.bool,
  onMobileTap: PropTypes.func,
  onMouseEnter: PropTypes.func,
};

function buildLookup(rows) {
  const lookup = {};
  if (rows) {
    rows.forEach(row => {
      row.cells.forEach((cell, dayIndex) => {
        if (cell.state === CELL_STATE.EARNINGS) {
          lookup[`${row.time}|${dayIndex}`] = cell.value;
        }
      });
    });
  }
  return lookup;
}

function Heatmap({
  currencyCode,
  rows,
  payoutRateRows,
  totalEarningsRows,
  campaignsPath,
  opportunitiesPath,
  isMobile,
  onCellTap,
  onCloseMobileSheet,
  classLabel,
  venueId,
}) {
  const currency = buildEarningsFormatter(currencyCode);
  const currencyCompact = buildEarningsFormatter(currencyCode, { compact: true });
  const [activeTooltip, setActiveTooltip] = useState(null);
  const { data: faqs = [] } = useSmartRateFaqsQuery();
  const hasUnfilledSpotsFaq = isSmartRateFaqPublished(faqs, SMART_RATE_FAQ_IDS.UNFILLED_SPOTS);

  const payoutRateLookup = buildLookup(payoutRateRows);
  const totalEarningsLookup = buildLookup(totalEarningsRows);

  return (
    <>
      <DayHourHeatmapGrid
        className={element('heatmap-table')}
        renderCell={(row, dayIndex, dayKey) => {
          const cell = row.cells[dayIndex];
          const key = `${row.time}|${dayIndex}`;
          const payoutRate = payoutRateLookup[key] ?? null;
          const totalEarnings = totalEarningsLookup[key] ?? null;
          const dayLabel = cell.day || dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
          const tooltip =
            cell.state !== CELL_STATE.EMPTY
              ? buildTooltip({
                  cell,
                  rowTime: row.time,
                  dayLabel,
                  averagePayoutRate: payoutRate,
                  totalEarnings,
                  currency,
                  hasUnfilledSpotsFaq,
                  onUnfilledSpotsClick: isMobile ? onCloseMobileSheet : () => setActiveTooltip(null),
                })
              : null;

          const handleMobileTap = () => onCellTap({ tooltip, campaignsPath, opportunitiesPath, classLabel });

          return (
            <HeatmapCell
              cell={cell}
              currency={currency}
              currencyCompact={currencyCompact}
              isMobile={isMobile}
              onMobileTap={handleMobileTap}
              onMouseEnter={tooltip ? () => setActiveTooltip(tooltip) : undefined}
              tooltip={tooltip}
              tooltipName={HEATMAP_TOOLTIP_NAME}
            />
          );
        }}
        rows={rows}
      />
      {!isMobile && (
        <TooltipContent
          className={element('tooltip-content')}
          clickable
          delayHide={250}
          delayUpdate={0}
          name={HEATMAP_TOOLTIP_NAME}
          place="top">
          {activeTooltip && (
            <WeeklyEarningsTooltip
              campaignsPath={campaignsPath}
              opportunitiesPath={opportunitiesPath}
              tooltip={activeTooltip}
              venueId={venueId}
            />
          )}
        </TooltipContent>
      )}
    </>
  );
}
Heatmap.propTypes = {
  currencyCode: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      time: PropTypes.string.isRequired,
      cells: PropTypes.arrayOf(PropTypes.shape({ state: PropTypes.string.isRequired })).isRequired,
    })
  ).isRequired,
  payoutRateRows: PropTypes.arrayOf(PropTypes.shape({})),
  totalEarningsRows: PropTypes.arrayOf(PropTypes.shape({})),
  campaignsPath: PropTypes.string.isRequired,
  opportunitiesPath: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  onCloseMobileSheet: PropTypes.func,
  classLabel: PropTypes.string,
  venueId: PropTypes.number,
  onCellTap: PropTypes.func.isRequired,
};

export default Heatmap;
